import "../../sections-client/ClientForms.scss";
import InfoContext from "pages/InfoContext";
import InputText from "core/inputs/input-text/InputText";
import { Controller } from "utils/Controller";
import { FormattedMessage } from "react-intl";
import { INPUTS_IDS, LANG } from "constants/constants";
import { Typography } from "core/typography/Typography";
import { useContext, useEffect, useMemo } from "react";
import {
  renderInputAddress,
  renderInputAffiliate,
  renderInputCity,
  renderInputCountry,
  renderInputPicture,
  renderInputPostalCode,
  renderInputAuthorizedManagers,
  renderInputSector,
} from "../CommonInputs";
import { CLIENT_FORM_LABELS, CLIENT_FORM_PLACEHOLDERS } from "constants/forms";
import { PropTypes } from "prop-types";
import ToggleButton from "core/buttons/toggle-button/ToggleButton";
import { useGeneralContext } from "api/directory/general/GeneralProvider";

const ClientPersonalInfoForm = ({
  control,
  errors,
  readOnly,
  register,
  setValue,
  watch,
}) => {
  const {
    authorizedManager,
    clientsList,
    getClients,
    getManagers,
    id,
    lang,
    sectorList,
    cellHighlight,
    isStrategicClient,
    setIsStrategicClient,
    picture,
    createFile,
    uploadProfilePicture,
    createdFile,
    setCreatedFile,
    setCode,
    setStatus,
    tempUserId,
    isValidated,
    dataCountry,
    apiErrorResponse,
  } = useContext(InfoContext);
  const { getNamesByFilters, namesByFilters } = useGeneralContext();

  const pictureValue = useMemo(() => {
    return createdFile?.picture_file_id || createdFile?.uri ? picture : "";
  }, [createdFile?.picture_file_id, createdFile?.uri, picture]);

  const clientCreationData = localStorage.getItem("clientCreationData");

  useEffect(() => {
    if (clientCreationData && sectorList.length > 0) {
      const parsedData = JSON.parse(clientCreationData);
      setValue("name", parsedData.client_name);

      const foundSector = sectorList.find(
        (sector) => sector.id === parsedData.sector_id
      );
      if (foundSector) {
        setValue("sector_id", [
          {
            label: lang === LANG.FR ? foundSector.fr : foundSector.en,
            value: foundSector.id,
          },
        ]);
      }
      localStorage.removeItem("clientCreationData");
    }
  }, [sectorList]);

  useEffect(() => {
    setValue(
      "picture_file_id",
      createdFile?.picture_file_id || createdFile?.uri
    );
  }, [createdFile]);

  const renderInputClientName = () => (
    <Controller
      {...{
        control,
        register,
        name: "name",
        rules: {
          required: true,
        },
        render: (props) => (
          <InputText
            id="name"
            errors={errors["name"]}
            readOnly={readOnly}
            label={CLIENT_FORM_LABELS.name}
            placeholder={CLIENT_FORM_PLACEHOLDERS.name}
            required
            {...props}
          />
        ),
      }}
    />
  );

  const centerBlockFormFields = [
    {
      label: CLIENT_FORM_LABELS.country,
      placeholder: CLIENT_FORM_PLACEHOLDERS.country,
      readOnly,
      control,
      register,
      errors,
      style: { borderRadius: id ? "0 0 0 6px" : "unset" },
      id: "country",
      countries: dataCountry,
      value: watch("country"),
      setValue,
      lang,
    },
    {
      label: CLIENT_FORM_LABELS.city,
      placeholder: CLIENT_FORM_PLACEHOLDERS.city,
      readOnly,
      register,
      control,
      id: "city",
      errors,
    },
    {
      label: CLIENT_FORM_LABELS.postal_code,
      placeholder: CLIENT_FORM_PLACEHOLDERS.postal_code,
      readOnly,
      errors,
      register,
      control,
      id: "postal_code",
    },
  ];

  const lineAuthorizedManagersAndAffiliate = [
    {
      label: CLIENT_FORM_LABELS.affiliate_id,
      getOptions: (search) => getClients({ search }),
      clientsList: clientsList?.map((elm) => ({
        label: elm?.name,
        value: elm?.id,
      })),
      readOnly,
      control,
      register,
      errors,
      setValue,
      value: watch("affiliate_id"),
      id: "affiliate_id",
    },
    {
      label: CLIENT_FORM_LABELS.assigned_managers,
      getOptions: getManagers,
      personsList: authorizedManager,
      readOnly,
      control,
      selectedOptions: watch("assigned_managers"),
      name: "assigned_managers",
      register,
      setValue,
      value: watch("assigned_managers"),
      id: "assigned_managers",
    },
  ];

  const renderInputField = (field) => {
    switch (field.label.id) {
      case CLIENT_FORM_LABELS.country.id:
        return renderInputCountry(field);
      case CLIENT_FORM_LABELS.city.id:
        return renderInputCity(field);
      default:
        return renderInputPostalCode(field);
    }
  };

  return (
    <div className="section-1">
      {!id && (
        <div
          className="strategic-client-toggle"
          style={{ marginBottom: "32px" }}
        >
          <ToggleButton
            id="strategic-client"
            label={
              <FormattedMessage
                id="strategic-client"
                defaultMessage="Client stratégique"
              />
            }
            readOnly={readOnly}
            onChange={(val) => setIsStrategicClient(val)}
            value={isStrategicClient}
          />
        </div>
      )}
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={<FormattedMessage id="whats_it" defaultMessage="WHAT’S IT ?" />}
      />
      <div className="client-personal-info-form">
        <div className="left-block">
          {!id && (
            <div
              className={`corner-left ${
                !picture && cellHighlight && "errorCellColor"
              }`}
            >
              {renderInputPicture({
                label: { id: "input_logo_label" },
                readOnly,
                id: INPUTS_IDS.CLIENT,
                value: pictureValue,
                createFile,
                uploadProfilePicture,
                setCreatedFile,
                required: true,
                setCode,
                setStatus,
                userId: tempUserId,
                isValidated,
                createdFile,
              })}
            </div>
          )}
          <div className="center-block">
            <div
              className={`cell-padding ${
                !watch("name") && cellHighlight && "errorCellColor"
              }`}
              style={{ borderRadius: id ? "6px 0 0 0" : "0" }}
            >
              {renderInputClientName()}
            </div>
            <div
              className={`cell-padding ${
                !watch("sector_id") && cellHighlight && "errorCellColor"
              }`}
            >
              {renderInputSector({
                control,
                register,
                setValue,
                value: watch("sector_id")?.[0]?.value || "",
                errors,
                readOnly,
                sectorList,
                lang,
              })}
            </div>
            {centerBlockFormFields.map((field) => (
              <div
                key={field.label.id}
                className={`cell-padding ${
                  !watch(field.label.id) && cellHighlight && "errorCellColor"
                }`}
                style={field.style}
              >
                {renderInputField(field)}
              </div>
            ))}
          </div>
          <div
            className={`corner-right ${
              !watch("sectoraddress_id") && cellHighlight && "errorCellColor"
            }`}
          >
            {renderInputAddress({
              control,
              errors,
              label: CLIENT_FORM_LABELS.address,
              placeholder: CLIENT_FORM_PLACEHOLDERS.address,
              readOnly,
              register,
              id: CLIENT_FORM_LABELS.address.id,
            })}
          </div>
        </div>
        <div className="right-block">
          <Typography
            className="title-3 violet text-uppercase"
            type="h2"
            label={
              <FormattedMessage id="assignment" defaultMessage="Affectation" />
            }
          />
          <div className="line">
            {lineAuthorizedManagersAndAffiliate?.map((field) => (
              <div
                key={field.label.id}
                className={`cell-padding ${
                  errors?.[field.id] || apiErrorResponse?.[field.id]
                    ? "errorCellColor"
                    : ""
                }`}
              >
                {field.label.id === CLIENT_FORM_LABELS.affiliate_id.id
                  ? renderInputAffiliate({
                      ...field,
                      errors:
                        errors["affiliate_id"] ||
                        apiErrorResponse?.["affiliate_id"],
                      errorMessage:
                        apiErrorResponse?.["affiliate_id"] ||
                        errors?.["affiliate_id"]?.message,
                    })
                  : renderInputAuthorizedManagers({
                      control,
                      label: field.label,
                      personsList: field.personsList,
                      readOnly: field.readOnly,
                      register,
                      setValue,
                      value: field.value,
                      errors:
                        errors["assigned_managers"] ||
                        apiErrorResponse?.["assigned_managers"],
                      errorMessage:
                        apiErrorResponse?.["assigned_managers"] ||
                        errors?.["assigned_managers"]?.message,
                      getNamesByFilters,
                      namesByFilters,
                    })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientPersonalInfoForm;

ClientPersonalInfoForm.propTypes = {
  control: PropTypes.object,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  register: PropTypes.func,
  setValue: PropTypes.func,
  watch: PropTypes.func,
};
